import { useState, useEffect, useCallback } from 'react';
import { Theme, IconButton, Tooltip } from '@mui/material';
import { AddComment as AddCommentIcon } from '@mui/icons-material';

import useCommentsContext, { IAnchor } from './useCommentsContext';

export default function AddCommentButton() {
  const { quillRef, onAddComment, comments } = useCommentsContext();

  const [ isShown, setIsShown ] = useState(false);
  const [ selectedAnchor, setSelectedAnchor ] = useState<IAnchor | null>(null);
  const [ topPositions, setTopPosition ] = useState(0);

  const handleSelectionChange = useCallback((range: any) => {
    if (range && range.length > 0) {
      // Do not allow adding comment to the text that already has comment
      const quill = quillRef.current?.getQuill();
      const hasFormat = quill
        ? quill.getFormat(range.index, range.length).comment
        : false
      ;
      if ( hasFormat ){
        setIsShown(false);
        return;
      };
      const bounds = quill.getBounds(range.index, range.length);
      setSelectedAnchor({ o: range.index, l: range.length });
      setIsShown(true);
      setTopPosition(bounds.top -12);
    } else {
      setIsShown(false);
      setSelectedAnchor(null);
      setTopPosition(0);
    }
  }, [quillRef, setIsShown]);

  useEffect(() => {
    const quill = quillRef.current?.getQuill();
    if ( !quill ) return;
    quill.on('selection-change', handleSelectionChange);
    return () => {
      quill.off('selection-change', handleSelectionChange);
    };
  }, [quillRef, handleSelectionChange]);

  const handleClick = () => {
    if ( !selectedAnchor ) return;
    onAddComment('', selectedAnchor);
    setIsShown(false);
  }

  if ( !isShown ) return null;
  return (
    <Tooltip title="Add comment" placement="right">
      <IconButton
        sx={(theme:Theme) => ({
          position: 'absolute',
          top: topPositions, left: comments.length ? -37 : -30, // 37 = 16px gap + 21px half button size
          backgroundColor: 'white',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '50%',
          zIndex: 1050,
          '&:hover': {
            backgroundColor: theme.palette.primary.light,
          }
        })}
        color="primary"
        onClick={handleClick}
      ><AddCommentIcon /></IconButton>
    </Tooltip>
  )
}
